import { AnalyticsCard } from '@/components/analytics/AnalyticsCard'
import { ChartBadge } from '@/components/analytics/ChartBadge'
import { StackedBarChart } from '@/components/analytics/StackedBarChart.tsx'
import { STORIES_ACCEPTED_CONFIG } from '@/components/analytics/StackedBarChartConfig.tsx'
import { Button } from '@/components/ui/button'
import { STORY_STATUSES } from '@/constants/STORY_STATUSES'
import { STORY_TYPES } from '@/constants/STORY_TYPES'
import { useUserContext } from '@/contexts/UserContext'
import { useProjectId } from '@/hooks/useProjectId'
import { useStackedBarChartData } from '@/hooks/useStackedBarChartData.ts'
import { useQueryAnalyticsIterations } from '@/requests/queries/useQueryAnalytics'
import { useNavigate } from 'react-router-dom'

export const StoriesAccepted = () => {
  const navigate = useNavigate()
  const projectId = useProjectId()
  const { apiKey } = useUserContext()
  const { iterations } = useQueryAnalyticsIterations(apiKey)
  const { storiesAcceptedData } = useStackedBarChartData(iterations)

  const currentIteration = iterations?.[iterations.length - 1]
  if (!currentIteration) return null

  const acceptedStories = currentIteration.stories.filter(
    (s) => s.status === STORY_STATUSES.ACCEPTED && s.storyType !== STORY_TYPES.RELEASE,
  )
  const acceptedFeatures = acceptedStories.filter((s) => s.storyType === STORY_TYPES.FEATURE)
  const acceptedDesigns = acceptedStories.filter((s) => s.storyType === STORY_TYPES.DESIGN)
  const acceptedChores = acceptedStories.filter((s) => s.storyType === STORY_TYPES.CHORE)
  const acceptedBugs = acceptedStories.filter((s) => s.storyType === STORY_TYPES.BUG)

  return (
    <AnalyticsCard
      title="Stories Accepted"
      subtitle="The total number of user stories accepted in a given iteration"
      contents={
        <>
          <Button
            type="button"
            variant="text"
            size="min"
            className="absolute top-[23px] right-[24px]"
            onClick={() => {
              navigate(`/project/${projectId}/analytics/stories-accepted`)
            }}
          >
            view report
          </Button>

          <div className="flex flex-col md:flex-row md:justify-between md:items-middle gap-4">
            <div className="flex flex-col gap-1">
              <div role="note" className="flex gap-1 items-end">
                <p className="font-EN-KR-heading1-bold text-contents-accent">{acceptedStories.length}</p>
                <p className="font-EN-KR-caption-regular text-contents-primary mb-1.5">stories</p>
              </div>
              <div className="flex flex-col gap-1">
                <ChartBadge variant="Feature" value={`${acceptedFeatures.length} Feature`} />
                <ChartBadge variant="Design" value={`${acceptedDesigns.length} Design`} />
                <ChartBadge variant="Chore" value={`${acceptedChores.length} Chore`} />
                <ChartBadge variant="Bug" value={`${acceptedBugs.length} Bug`} />
              </div>
            </div>

            <div className="flex-1 h-[180px] min-w-[240px] " aria-label="Stories Accepted chart">
              <StackedBarChart data={storiesAcceptedData} config={STORIES_ACCEPTED_CONFIG} height={180} />
            </div>
          </div>
        </>
      }
    />
  )
}
